import type { IconName } from '../../components';
import type { FeatureFlagKey } from '../../config/featureFlags';
import { ROUTE } from '../../routes';
import type { MarketingHubArea } from '../../theme/tokens';

export type MarketingAreaId = 'banner-kits' | 'html5-banners' | 'videos' | 'playables' | 'landing-pages';

// Compile-time check: every area has hub colours (`MARKETING_HUB_AREA` is keyed by `MarketingHubArea`), so an area
// added here without its tokens fails the typecheck instead of rendering an uncoloured card.
const _areaCheck: MarketingHubArea = null as unknown as MarketingAreaId;

/** A tile glyph: one of the shared `Icon`s, or one of the hub's own (see `components/Glyphs`). */
export type MarketingGlyph = { kind: 'icon'; name: IconName } | { kind: 'own'; name: 'html5' | 'video' | 'mini-game' };

export type MarketingArea = {
  id: MarketingAreaId;
  glyph: MarketingGlyph;
  /** The area's library: where its card goes. */
  path: string;
  /** The card shows only while this flag is on, the flag its route is gated on (the route answers 404 otherwise). */
  flag: FeatureFlagKey;
};

/**
 * The Marketing hub's areas, in the order the section's tabs had: Image Banners, HTML5 Banners, Video Ads, Mini-games,
 * then Landing Pages.
 * The older Image Ads studio has no card: it shows only while Image Banners is off and has no route of its own, and
 * every Marketing flag is generally available (their rollback switches all re-gate behind the plain "777"), so the two
 * are never apart in practice.
 */
export const MARKETING_AREAS: readonly MarketingArea[] = [
  {
    id: 'banner-kits',
    glyph: { kind: 'icon', name: 'image' },
    path: ROUTE.creativesBannerKits(),
    flag: 'creatives-banner-kits',
  },
  {
    id: 'html5-banners',
    glyph: { kind: 'own', name: 'html5' },
    path: ROUTE.creativesHtml5Banners(),
    flag: 'creatives-html5-banners',
  },
  { id: 'videos', glyph: { kind: 'own', name: 'video' }, path: ROUTE.creativesVideos(), flag: 'creatives-video' },
  {
    id: 'playables',
    glyph: { kind: 'own', name: 'mini-game' },
    path: ROUTE.creativesPlayables(),
    flag: 'creatives-playables',
  },
  {
    id: 'landing-pages',
    glyph: { kind: 'icon', name: 'globe' },
    path: ROUTE.creativesLandingPages(),
    flag: 'creatives-landing-pages',
  },
];

/** The areas to show, in order: each shows under exactly the flag that shows its library. Video Ads also needs its
 *  per-beat price (`videoPriced`): the page offers its library only once that is known (its data-driven switch), so a
 *  card shown without it would open another area's library under the Video Ads URL. */
export const visibleMarketingAreas = (
  isEnabled: (key: string | undefined) => boolean,
  videoPriced: boolean,
): MarketingArea[] => MARKETING_AREAS.filter((area) => isEnabled(area.flag) && (area.id !== 'videos' || videoPriced));
